import { LoadingOutlined } from "@ant-design/icons";
import uniqueId from "lodash/uniqueId";
import { ComponentPropsWithRef, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Download01 } from "@sol/uikit/core/icons/General/Download01";
import { Spin } from "@sol/uikit/feedback/Spin/Spin";
import { Button } from "@sol/uikit/general/Button/Button";

import { DownloadProgress } from "./useDownload";

type Props = {
    /** Whether a download is currently running. */
    isPending?: boolean;
    /** Completion percentage, or null while the total size is still unknown. */
    progress?: DownloadProgress;
} & Omit<ComponentPropsWithRef<typeof Button>, "loading" | "icon" | "iconPosition">;

/**
 * Button carrying the download affordance: a download icon at rest, replaced
 * while a download runs by a spinner that turns determinate as soon as
 * `progress` is known. Pair it with `useDownload`, which supplies both states.
 */
export function DownloadButton({ isPending = false, progress = null, children, ...rest }: Props) {
    const [t] = useTranslation();
    // React 17: no `useId` to derive a stable id for `aria-describedby` from.
    const progressId = useMemo(() => uniqueId("download-progress-"), []);
    const hasProgress = isPending && progress !== null;

    /**
     * The percent ring is drawn by Spin's built-in indicator only, so the custom
     * one is limited to the indeterminate state.
     *
     * Both are hidden from assistive tech, as pure decoration: a button is named
     * after its contents, and each would leak into that name. The custom
     * indicator ships a hardcoded English `aria-label="loading"`, and the
     * built-in ring exposes a `role="progressbar"` whose `aria-valuenow` the
     * name computation substitutes in — renaming the button on every percent.
     * The semantics live outside the button instead, below.
     */
    const loadingIcon = (
        <span aria-hidden="true">
            {progress === null ? (
                <Spin indicator={<LoadingOutlined spin />} size="small" tip={null} />
            ) : (
                <Spin size="small" tip={null} percent={progress} />
            )}
        </span>
    );

    return (
        <>
            <Button
                aria-busy={isPending}
                // Surfaces the percentage on focus. A description, unlike a
                // name, is not re-announced every time it changes.
                aria-describedby={hasProgress ? progressId : undefined}
                loading={isPending && { icon: loadingIcon }}
                icon={
                    <span role="img" className="anticon" aria-hidden="true">
                        <Download01 size={16} />
                    </span>
                }
                {...rest}
            >
                {children}
            </Button>
            {/*
             * Kept outside the button: a button is named after its contents, so
             * a message in there would be appended to its accessible name.
             * Always rendered, never mounted along with its content either — a
             * live region inserted at the same time as the message it carries is
             * not reliably announced. Only the start is announced: `isPending`
             * drops on failure too, and the caller already reports that.
             */}
            <span className="sr-only" aria-live="polite">
                {isPending ? t("shared.download.DownloadButton.loading") : ""}
            </span>
            {/*
             * Queried on demand rather than announced, so it can be mounted
             * with the download and carry a value that changes continuously.
             */}
            {hasProgress && (
                <span
                    id={progressId}
                    className="sr-only"
                    role="progressbar"
                    aria-label={t("shared.download.DownloadButton.progress")}
                    aria-valuenow={progress}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    /*
                     * A range's value replaces its text wherever its name or
                     * description is computed, so a bare "42" would be all the
                     * button's description carried. `aria-valuetext` is what
                     * gets substituted instead when set.
                     */
                    aria-valuetext={t("shared.download.DownloadButton.progressValue", { percent: progress })}
                />
            )}
        </>
    );
}
